/* MANGO MOJITO Book a fitting (design system: FittingConcept, fitting.md). No past dates and no day the chosen store is
   closed; the times are the chosen store's fitting hours, hour by hour, without today's past hours (with one store they
   show at once). The model is filled in
   when the customer came from a product page (?product=handle&variant=id). Before sending, each missing or wrong field
   gets one polite sentence, the count goes above the form and focus moves to the first; a good form is sent to Shopify's
   contact form and the thank-you repeats the request. */
class MmFitting extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-mm-fitting-form]');
    const done = this.querySelector('[data-mm-fitting-done]');
    if (done) { this.showDone(done); return; }
    if (!this.form) return;

    this.date = this.form.querySelector('[data-mm-date]');
    this.times = this.form.querySelector('[data-mm-times]');
    this.timeHint = this.form.querySelector('[data-mm-time-hint]');
    this.date.min = this.iso(new Date());

    this.form.addEventListener('change', (e) => {
      if (e.target.matches('[data-mm-store], [data-mm-date]')) this.renderTimes();
      if (e.target.closest('[data-mm-field]')) this.clearError(e.target.closest('[data-mm-field]').dataset.mmField);
    });
    this.form.addEventListener('submit', (e) => { if (!this.validate()) e.preventDefault(); else this.remember(); });
    this.renderTimes();
    this.prefill();
  }

  iso(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }

  // one store is a hidden field (always chosen); two or more are radio cards
  store() { return this.form.querySelector('input[type="hidden"][data-mm-store], [data-mm-store]:checked'); }

  closedOn(store, dateValue) {
    if (!store || !dateValue) return false;
    const [y, m, d] = dateValue.split('-').map(Number);
    const day = new Date(y, m - 1, d).getDay();
    return store.dataset.closed.split(',').filter(Boolean).map(Number).includes(day);
  }

  // the chosen store's hours, one pill an hour; today's past hours left out
  renderTimes() {
    const store = this.store();
    const chosen = (this.form.querySelector('input[name="contact[Time]"]:checked') || {}).value;
    this.times.textContent = '';
    if (this.timeHint) this.timeHint.hidden = !!store;
    if (!store) return;
    const from = Number(store.dataset.from);
    const to = Number(store.dataset.to);
    const now = new Date();
    const isToday = this.date.value === this.iso(now);
    for (let hour = from; hour < to; hour++) {
      if (isToday && hour <= now.getHours()) continue;
      const value = `${String(hour).padStart(2, '0')}:00`;
      const label = document.createElement('label');
      label.className = 'mm-pill';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'contact[Time]';
      input.value = value;
      input.checked = value === chosen;
      label.append(input, value);
      this.times.append(label);
    }
  }

  // the model the customer was looking at, from the product page link
  async prefill() {
    const params = new URLSearchParams(window.location.search);
    const handle = params.get('product');
    const cameFrom = this.form.querySelector('[data-mm-came-from]');
    if (cameFrom) cameFrom.value = handle ? `Product page: ${handle}` : (document.referrer ? `Link from ${new URL(document.referrer).pathname}` : 'Direct');
    const model = this.form.querySelector('[data-mm-model]');
    if (!handle || !model || model.value) return;
    try {
      const product = await fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}products/${encodeURIComponent(handle)}.js`).then((r) => r.json());
      const variant = product.variants.find((v) => String(v.id) === params.get('variant'));
      const parts = [product.title];
      // a bare number is a size: say EU 42, as the product page does
      if (variant && variant.title && variant.title !== 'Default Title') parts.push(...variant.title.split(' / ').map((p) => (/^\d{2}(\.5)?$/.test(p) ? `EU ${p}` : p)));
      if (!model.value) model.value = parts.join(' · ');
    } catch (err) { /* the field stays empty and editable */ }
  }

  setError(key, on) {
    const err = this.form.querySelector(`[data-mm-err="${key}"]`);
    if (err) err.hidden = !on;
    const field = this.form.querySelector(`[data-mm-field="${key}"]`);
    if (field) field.querySelectorAll('.mm-field, .mm-check').forEach((f) => f.classList.toggle('has-error', on));
    if (field) field.querySelectorAll('input, textarea').forEach((i) => i.toggleAttribute('aria-invalid', on));
  }

  clearError(key) {
    this.setError(key, false);
    if (key === 'date') this.setError('closed', false);
  }

  validate() {
    const f = this.form;
    const val = (name) => ((f.querySelector(`[name="${name}"]`) || {}).value || '').trim();
    const store = this.store();
    const date = this.date.value;
    const errors = {
      store: !store,
      date: !date || date < this.date.min,
      closed: !!date && this.closedOn(store, date),
      time: !f.querySelector('input[name="contact[Time]"]:checked'),
      name: !val('contact[name]'),
      phone: val('contact[phone]').replace(/\D/g, '').length < 9,
      email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val('contact[email]')),
      consent: !f.querySelector('[name="contact[Consent]"]').checked,
    };
    let count = 0;
    let first = null;
    Object.entries(errors).forEach(([key, bad]) => {
      this.setError(key, bad);
      if (!bad) return;
      count += 1;
      if (!first) first = f.querySelector(`[data-mm-field="${key === 'closed' ? 'date' : key}"]`);
    });
    const summary = this.querySelector('[data-mm-summary]');
    summary.hidden = count === 0;
    summary.textContent = count === 1 ? this.dataset.check1 : this.dataset.check.replace('__N__', count);
    if (first) {
      const target = first.querySelector('input, textarea') || first;
      target.focus();
      first.scrollIntoView({ block: 'center', behavior: document.documentElement.classList.contains('mm-motion') ? 'smooth' : 'auto' });
    }
    return count === 0;
  }

  // keep the request for the thank-you (Shopify's contact form returns to this page without it)
  remember() {
    const f = this.form;
    const val = (name) => ((f.querySelector(`[name="${name}"]:checked`) || f.querySelector(`[name="${name}"]:not([type="radio"])`) || {}).value || '').trim();
    const request = {
      name: val('contact[name]'),
      rows: [
        [this.dataset.rowStore, val('contact[Store]')],
        [this.dataset.rowDate, this.date.value ? new Date(`${this.date.value}T00:00`).toLocaleDateString(document.documentElement.lang || undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''],
        [this.dataset.rowTime, val('contact[Time]')],
        [this.dataset.rowReason, val('contact[What would you like to do]')],
        [this.dataset.rowModel, val('contact[Model]')],
      ].filter((r) => r[1]),
    };
    try { sessionStorage.setItem('mm-fitting', JSON.stringify(request)); } catch (err) { /* the thank-you stays general */ }
  }

  showDone(done) {
    let request = null;
    try { request = JSON.parse(sessionStorage.getItem('mm-fitting') || 'null'); sessionStorage.removeItem('mm-fitting'); } catch (err) { request = null; }
    if (request) {
      if (request.name) done.querySelector('[data-mm-done-title]').textContent = this.dataset.thanks.replace('__NAME__', request.name.split(' ')[0]);
      const list = done.querySelector('[data-mm-done-rows]');
      request.rows.forEach(([term, detail]) => {
        const row = document.createElement('div');
        row.className = 'mm-specs__row';
        const dt = document.createElement('dt');
        dt.textContent = term;
        const dd = document.createElement('dd');
        dd.textContent = detail;
        row.append(dt, dd);
        list.append(row);
      });
      list.hidden = request.rows.length === 0;
    }
    done.focus();
  }
}

if (!customElements.get('mm-fitting')) customElements.define('mm-fitting', MmFitting);
